import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import './i18n';
import App from './App';
import { ToastProvider } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';

// 전역 Promise 오류 핸들러
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  
  // Chrome 확장 프로그램 오류 필터링 (더 포괄적으로)
  const isExtensionError = 
    // runtime.lastError 관련
    reason?.message?.includes('runtime.lastError') ||
    // 확장 프로그램 스크립트 파일
    reason?.stack?.includes('content.js') ||
    reason?.stack?.includes('background.js') ||
    reason?.stack?.includes('extension://') ||
    reason?.stack?.includes('chrome-extension://') ||
    reason?.originalError?.stack?.includes('background.js') ||
    reason?.originalError?.stack?.includes('content.js') ||
    reason?.originalError?.stack?.includes('extension://') ||
    reason?.originalError?.stack?.includes('chrome-extension://') ||
    // 확장 프로그램 API 경로
    reason?.reqInfo?.pathPrefix?.includes('/site_integration') ||
    reason?.reqInfo?.pathPrefix?.includes('/extension') ||
    // 확장 프로그램 관련 오류 코드 패턴
    (reason?.name === 'i' && reason?.reqInfo) ||
    (reason?.name === 'i' && reason?.httpStatus === 200 && reason?.code === 403) ||
    // reqInfo가 있고 앱 경로가 아닌 경우
    (reason?.reqInfo && reason?.reqInfo?.pathPrefix && !reason?.reqInfo?.pathPrefix?.startsWith('/api')) ||
    // originalError가 있고 확장 프로그램 관련인 경우
    (reason?.originalError && (
      reason.originalError.stack?.includes('background.js') ||
      reason.originalError.stack?.includes('content.js') ||
      reason.originalError.stack?.includes('extension://') ||
      reason.originalError.stack?.includes('chrome-extension://')
    ));
  
  if (isExtensionError) {
    // 확장 프로그램 오류는 조용히 무시하고 기본 오류 처리 방지
    event.preventDefault();
    return;
  }
  
  // 앱 코드에서 발생한 실제 오류만 로깅
  console.error('Unhandled Promise Rejection (App Error):', reason);
  
  // 개발 환경에서만 상세 로그
  if (process.env.NODE_ENV === 'development') {
    console.error('Error details:', {
      reason: reason,
      promise: event.promise,
      stack: reason?.stack
    });
  }
}, true); // capture phase에서 먼저 처리

// 전역 오류 핸들러
window.addEventListener('error', (event) => {
  // Chrome 확장 프로그램 오류는 무시
  const isExtensionError = 
    event.message?.includes('runtime.lastError') ||
    event.filename?.includes('extension://') ||
    event.filename?.includes('chrome-extension://') ||
    event.filename?.includes('content.js') ||
    event.filename?.includes('background.js');
  
  if (isExtensionError) {
    // 확장 프로그램 오류는 조용히 무시
    return;
  }
  
  // 앱 코드에서 발생한 실제 오류만 로깅
  console.error('Global error (App Error):', event.error);
  
  // 개발 환경에서만 상세 로그
  if (process.env.NODE_ENV === 'development') {
    console.error('Error details:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

// PWA Service Worker 등록
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => console.log('SW registered:', reg.scope))
      .catch((err) => console.log('SW registration failed:', err));
  });
} 